import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Popconfirm,
  Tooltip,
  Switch,
  Radio,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  LinkOutlined,
  UserOutlined,
  GlobalOutlined,
} from '@ant-design/icons';

const { Option } = Select;

type BindType = 'app' | 'h5';

interface DomainRecord {
  id: number;
  bindType: BindType; // 绑定途径
  domain?: string; // h5需要域名，app不需要
  channelId?: number;
  channelName?: string;
  agentId?: string;
  agentName?: string;
  status: 'enabled' | 'disabled';
  remark?: string;
  createdAt: string;
  updatedAt: string;
  updater?: string;
}

const DomainManagement: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<DomainRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [form] = Form.useForm();

  // 模拟渠道数据（从渠道配置页面获取）
  const channelList = [
    { id: 1, channelName: '官方渠道', agentId: 'agent_001', agentName: '代理A' },
    { id: 2, channelName: '社交媒体渠道', agentId: undefined, agentName: undefined },
    { id: 3, channelName: '搜索引擎渠道', agentId: 'agent_002', agentName: '代理B' },
  ];

  // 模拟代理绑定数据
  const [domainData, setDomainData] = useState<DomainRecord[]>([
    {
      id: 1,
      bindType: 'h5',
      domain: 'promo001.example.com',
      channelId: 1,
      channelName: '官方渠道',
      agentId: 'agent_001',
      agentName: '代理A',
      status: 'enabled',
      remark: '主推广域名',
      createdAt: '2025-01-15 10:00:00',
      updatedAt: '2025-10-23 14:30:00',
    },
    {
      id: 2,
      bindType: 'app',
      channelId: 2,
      channelName: '社交媒体渠道',
      agentId: 'agent_002',
      agentName: '代理B',
      status: 'enabled',
      remark: 'APP绑定',
      createdAt: '2025-02-20 11:00:00',
      updatedAt: '2025-10-22 16:20:00',
    },
    {
      id: 3,
      bindType: 'h5',
      domain: 'promo003.example.com',
      channelId: 3,
      channelName: '搜索引擎渠道',
      agentId: 'agent_002',
      agentName: '代理B',
      status: 'disabled',
      remark: '测试域名',
      createdAt: '2025-03-10 09:00:00',
      updatedAt: '2025-10-21 10:15:00',
    },
    {
      id: 4,
      bindType: 'h5',
      domain: 'promo004.example.com',
      channelId: undefined,
      channelName: undefined,
      agentId: 'agent_003',
      agentName: '代理C',
      status: 'enabled',
      remark: '新推广域名',
      createdAt: '2025-09-01 14:00:00',
      updatedAt: '2025-10-20 09:30:00',
    },
    {
      id: 5,
      bindType: 'app',
      channelId: 1,
      channelName: '官方渠道',
      agentId: 'agent_004',
      agentName: '代理D',
      status: 'enabled',
      remark: 'APP绑定',
      createdAt: '2025-08-15 10:30:00',
      updatedAt: '2025-10-19 15:45:00',
    },
  ]);

  // 模拟代理列表
  const agentList = [
    { id: 'agent_001', name: '代理A' },
    { id: 'agent_002', name: '代理B' },
    { id: 'agent_003', name: '代理C' },
    { id: 'agent_004', name: '代理D' },
    { id: 'agent_005', name: '代理E' },
  ];

  // 过滤数据
  const filteredData = domainData.filter(item => {
    const matchesSearch = !searchText || 
      item.domain?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.agentName?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.channelName?.toLowerCase().includes(searchText.toLowerCase());
    const matchesStatus = filterStatus === 'all' || item.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // 统计数据
  const statsData = {
    total: filteredData.length,
    enabled: filteredData.filter(item => item.status === 'enabled').length,
    disabled: filteredData.filter(item => item.status === 'disabled').length,
    app: filteredData.filter(item => item.bindType === 'app').length,
    h5: filteredData.filter(item => item.bindType === 'h5').length,
  };

  const handleAdd = () => {
    setSelectedRecord(null);
    form.resetFields();
    setEditModalVisible(true);
  };

  const handleEdit = (record: DomainRecord) => {
    setSelectedRecord(record);
    form.setFieldsValue({
      ...record,
      status: record.status === 'enabled',
    });
    setEditModalVisible(true);
  };

  // 处理绑定途径变化
  const handleBindTypeChange = (bindType: BindType) => {
    // 清空相关字段
    form.setFieldsValue({
      domain: undefined,
      channelId: undefined,
      agentId: undefined,
    });
  };

  const handleDelete = (record: DomainRecord) => {
    setDomainData(domainData.filter(item => item.id !== record.id));
    const displayName = record.bindType === 'h5' ? record.domain : `${record.channelName} - ${record.agentName}`;
    message.success(`已删除代理绑定：${displayName}`);
  };

  const handleBatchDelete = () => {
    setDomainData(domainData.filter(item => !selectedRowKeys.includes(item.id)));
    setSelectedRowKeys([]);
    message.success(`已删除 ${selectedRowKeys.length} 个代理绑定`);
  };

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const selectedChannel = values.channelId 
        ? channelList.find(c => c.id === values.channelId)
        : null;

      const selectedAgent = values.agentId ? agentList.find(a => a.id === values.agentId) : null;

      const domainRecord: DomainRecord = {
        id: selectedRecord?.id || Date.now(),
        bindType: values.bindType,
        domain: values.bindType === 'h5' ? values.domain : undefined,
        channelId: values.channelId,
        channelName: selectedChannel?.channelName,
        agentId: values.agentId,
        agentName: selectedAgent?.name,
        status: values.status ? 'enabled' : 'disabled',
        remark: values.remark,
        createdAt: selectedRecord?.createdAt || new Date().toLocaleString('zh-CN'),
        updatedAt: new Date().toLocaleString('zh-CN'),
        updater: 'admin',
      };

      if (selectedRecord) {
        // 更新
        setDomainData(domainData.map(item => 
          item.id === selectedRecord.id ? domainRecord : item
        ));
        message.success('代理绑定修改成功！');
      } else {
        // 新增
        setDomainData([...domainData, domainRecord]);
        message.success('代理绑定添加成功！');
      }

      setEditModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('操作失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 处理渠道选择变化
  const handleChannelChange = (channelId: number | undefined) => {
    if (channelId) {
      const selectedChannel = channelList.find(c => c.id === channelId);
      if (selectedChannel) {
        // 如果渠道有代理，自动填充代理字段
        form.setFieldsValue({
          agentId: selectedChannel.agentId,
        });
      } else {
        // 如果渠道没有代理，清空代理字段
        form.setFieldsValue({
          agentId: undefined,
        });
      }
    } else {
      // 取消选择渠道，清空代理字段
      form.setFieldsValue({
        agentId: undefined,
      });
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    message.loading('正在刷新数据...', 1);
    setTimeout(() => {
      setLoading(false);
      message.success('数据刷新成功！');
    }, 1500);
  };

  const columns = [
    {
      title: '绑定途径',
      dataIndex: 'bindType',
      key: 'bindType',
      width: 100,
      render: (type: BindType) => (
        <Tag color={type === 'app' ? 'blue' : 'green'}>
          {type === 'app' ? 'APP' : 'H5'}
        </Tag>
      ),
    },
    {
      title: '域名',
      dataIndex: 'domain',
      key: 'domain',
      width: 250,
      render: (text: string, record: DomainRecord) => {
        if (record.bindType === 'h5' && text) {
          return (
            <Space>
              <GlobalOutlined />
              <span>{text}</span>
            </Space>
          );
        }
        return <span style={{ color: '#999' }}>-</span>;
      },
    },
    {
      title: '渠道',
      dataIndex: 'channelName',
      key: 'channelName',
      width: 150,
      render: (text: string) => text || '-',
    },
    {
      title: '绑定代理',
      dataIndex: 'agentName',
      key: 'agentName',
      width: 150,
      render: (text: string, record: DomainRecord) => {
        if (text) {
          return (
            <Space>
              <UserOutlined />
              <span>{text}</span>
              <Tag color="blue">{record.agentId}</Tag>
            </Space>
          );
        }
        return <span style={{ color: '#999' }}>未绑定</span>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'enabled' ? 'green' : 'red'}>
          {status === 'enabled' ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: any, record: DomainRecord) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个代理绑定吗？"
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <div style={{ padding: 24 }}>
      {/* 统计卡片 */}
      <Space size="large" style={{ marginBottom: 24 }}>
        <Card>
          <Space>
            <UserOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            <div>
              <div style={{ fontSize: 20, fontWeight: 'bold' }}>{statsData.total}</div>
              <div style={{ color: '#999', fontSize: 14 }}>总绑定数</div>
            </div>
          </Space>
        </Card>
        <Card>
          <Space>
            <GlobalOutlined style={{ fontSize: 24, color: '#52c41a' }} />
            <div>
              <div style={{ fontSize: 20, fontWeight: 'bold' }}>{statsData.enabled}</div>
              <div style={{ color: '#999', fontSize: 14 }}>已启用</div>
            </div>
          </Space>
        </Card>
        <Card>
          <Space>
            <GlobalOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />
            <div>
              <div style={{ fontSize: 20, fontWeight: 'bold' }}>{statsData.disabled}</div>
              <div style={{ color: '#999', fontSize: 14 }}>已禁用</div>
            </div>
          </Space>
        </Card>
        <Card>
          <Space>
            <UserOutlined style={{ fontSize: 24, color: '#722ed1' }} />
            <div>
              <div style={{ fontSize: 20, fontWeight: 'bold' }}>{statsData.app}</div>
              <div style={{ color: '#999', fontSize: 14 }}>APP绑定</div>
            </div>
          </Space>
        </Card>
        <Card>
          <Space>
            <GlobalOutlined style={{ fontSize: 24, color: '#13c2c2' }} />
            <div>
              <div style={{ fontSize: 20, fontWeight: 'bold' }}>{statsData.h5}</div>
              <div style={{ color: '#999', fontSize: 14 }}>H5绑定</div>
            </div>
          </Space>
        </Card>
      </Space>

      <Card
        title="代理绑定管理"
        extra={
          <Space>
            <Input
              placeholder="搜索域名或代理名称"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            <Select
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: 120 }}
            >
              <Option value="all">全部状态</Option>
              <Option value="enabled">已启用</Option>
              <Option value="disabled">已禁用</Option>
            </Select>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增代理绑定
            </Button>
            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`确定要删除选中的 ${selectedRowKeys.length} 个代理绑定吗？`}
                onConfirm={handleBatchDelete}
                okText="确定"
                cancelText="取消"
              >
                <Button danger icon={<DeleteOutlined />}>
                  批量删除
                </Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          scroll={{ x: 1200 }}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Card>

      {/* 编辑/新增模态框 */}
      <Modal
        title={selectedRecord ? '编辑代理绑定' : '新增代理绑定'}
        open={editModalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            bindType: 'app',
            status: 'enabled',
          }}
        >
          <Form.Item
            name="bindType"
            label="绑定途径"
            rules={[{ required: true, message: '请选择绑定途径' }]}
          >
            <Radio.Group onChange={(e) => handleBindTypeChange(e.target.value)}>
              <Radio value="app">APP</Radio>
              <Radio value="h5">H5</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.bindType !== currentValues.bindType}
          >
            {({ getFieldValue }) => {
              const bindType = getFieldValue('bindType');
              
              if (bindType === 'app') {
                // APP绑定：先选择渠道，然后选择代理
                return (
                  <>
                    <Form.Item
                      name="channelId"
                      label="渠道"
                      rules={[{ required: true, message: '请选择渠道' }]}
                    >
                      <Select 
                        placeholder="请选择渠道" 
                        allowClear
                        onChange={handleChannelChange}
                      >
                        {channelList.map(channel => (
                          <Option key={channel.id} value={channel.id}>
                            {channel.channelName}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name="agentId"
                      label="绑定代理"
                      rules={[{ required: true, message: '请选择绑定的代理' }]}
                    >
                      <Select 
                        placeholder="请选择代理"
                      >
                        {agentList.map(agent => (
                          <Option key={agent.id} value={agent.id}>
                            {agent.name} ({agent.id})
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </>
                );
              } else {
                // H5绑定：填写域名，然后选择渠道，最后填写代理
                return (
                  <>
                    <Form.Item
                      name="domain"
                      label="域名地址"
                      rules={[
                        { required: true, message: '请输入域名' },
                        {
                          pattern: /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9])+$/,
                          message: '请输入有效的域名格式',
                        },
                      ]}
                    >
                      <Input
                        prefix={<GlobalOutlined />}
                        placeholder="例如：promo.example.com"
                        disabled={!!selectedRecord}
                      />
                    </Form.Item>

                    <Form.Item
                      name="channelId"
                      label="渠道"
                    >
                      <Select 
                        placeholder="请选择渠道（非必填）" 
                        allowClear
                        onChange={handleChannelChange}
                      >
                        {channelList.map(channel => (
                          <Option key={channel.id} value={channel.id}>
                            {channel.channelName}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name="agentId"
                      label="绑定代理"
                      rules={[{ required: true, message: '请选择绑定的代理' }]}
                    >
                      <Select 
                        placeholder="请选择代理"
                      >
                        {agentList.map(agent => (
                          <Option key={agent.id} value={agent.id}>
                            {agent.name} ({agent.id})
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </>
                );
              }
            }}
          </Form.Item>

          <Form.Item
            name="status"
            label="状态"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="启用"
              unCheckedChildren="禁用"
            />
          </Form.Item>

          <Form.Item
            name="remark"
            label="备注"
          >
            <Input.TextArea
              rows={4}
              placeholder="请输入备注信息"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DomainManagement;

